<template>
    <div class="component session__edit_details">
        <form-radio-horizontal
            v-if="user.isTokenFromNewLms()"
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :name="'session-edit-details-session-type'"
            :label="'Session type'"
            :required="true"
            :v-validate="{ required: true }"
            :items="[
                {
                    name: 'Face-to-face',
                    value: 'face_to_face'
                },
                {
                    name: 'Virtual',
                    value: 'virtual'
                }
            ]"
            v-model="sessionSessionType"
        ></form-radio-horizontal>
        <div :class="[
                'session__edit_details_dates',
                `session__edit_details_dates--${sessionSessionType}`
            ]">
            <transition-group
                name="fade"
                mode="out-in"
                tag="ul"
            >
                <li
                    v-for="(item, index) in sessionOccurrenceItems"
                    :key="`key-${index}`"
                >
                    <div class="session__edit_details_dates_top">
                        <div class="columns">
                            <div class="column">
                                <component-title
                                    :size="'medium'"
                                    :underline="false"
                                    :raw="true"
                                >Date {{ index + 1 }}
                                </component-title>
                            </div>
                            <div class="column">
                                <template v-if="virtualOccurrenceActive !== index && item.dateStartLocal && item.dateEndLocal">
                                    <div class="columns">
                                        <div class="column is-narrow">
                                            {{ item.dateStartLocal.replaceAll('-', '/') }}
                                        </div>
                                        <div class="column is-narrow">
                                            {{ item.timeStartLocal }} - {{ item.timeEndLocal }} ({{ getHoursDifference(`${item.dateStartLocal} ${item.timeStartLocal}`, `${item.dateEndLocal} ${item.timeEndLocal}`, 'hr') }})
                                        </div>
                                    </div>
                                </template>
                            </div>
                            <div class="column is-narrow">
                                <component-button
                                    :class="'session__edit_details_dates_top_switch'"
                                    :name="`session-edit-details-dates-top-switch`"
                                    :icon="true"
                                    :icon-name="virtualOccurrenceActive === index ? 'chevron-down' : 'chevron-up'"
                                    :icon-raw="true"
                                    :theme="''"
                                    @click="setVirtualOccurrenceActive(index)"
                                ></component-button>
                            </div>
                        </div>
                    </div>
                    <div class="session__edit_details_dates_validation">
                        <form-text
                            :hidden="'true'"
                            :name="`hidden-${index}`"
                            :label="''"
                            :value="index"
                            :v-validate.continues="{ required: true, checkDatesConflict: [index] }"
                        />
                        <span
                            class="form__error form__text_error"
                            v-if="errors.first(`hidden-${index}`)"
                        >{{ errors.first(`hidden-${index}`) }}</span>
                    </div>
                    <transition name="collapse" mode="out-in">
                        <div
                            v-show="virtualOccurrenceActive === index"
                            class="session__edit_details_dates_main"
                        >
                            <template v-if="sessionSessionType === 'face_to_face'">
                                <div class="session__edit_details_dates_main_facetoface">
                                    <div class="columns is-variable is-4">
                                        <div class="column is-6">
                                            <div :class="['session__edit_details_dates_form', 'session__edit_details_dates_form--from', errors.first(`hidden-${index}`) ? 'session__edit_details_dates_form--error' : '']">
                                                <div class="columns columns--oneline is-variable is-2">
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_date">
                                                            <form-date
                                                                :ref="'fromDate'"
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="$t('ebs.sessions.edit.labels.labelFrom')"
                                                                :name="`date-from-${index}`"
                                                                :required="true"
                                                                :value="item.dateStartLocal"
                                                                :v-validate="`required|date_format:yyyy-MM-dd|${item.dateEndLocal && item.dateStartLocal !== item.dateEndLocal ? `before:${item.dateEndLocal}` :  ``}`"
                                                                :v-validate-custom-error="$t('ebs.sessions.edit.notify.validateCustomError')"
                                                                @input="[setSessionOccurrenceItemDateStart(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            >
                                                            </form-date>
                                                        </div>
                                                    </div>
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_time">
                                                            <form-time
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="' '"
                                                                :name="`time-from-${index}`"
                                                                :value="item.timeStartLocal"
                                                                :v-validate="{ required: true, date_format: 'HH:mm', before: (item.dateStartLocal === item.dateEndLocal && item.timeEndLocal) ? item.timeEndLocal : false }"
                                                                :placeholder="'00:00'"
                                                                :data-vv-as="$t('forms.dataVvAs.hour')"
                                                                @input="[setSessionOccurrenceItemTimeStart(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="column is-6">
                                            <div :class="['session__edit_details_dates_form', 'session__edit_details_dates_form--to', errors.first(`hidden-${index}`) ? 'session__edit_details_dates_form--error' : '']">
                                                <div class="columns columns--oneline is-variable is-2">
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_date">
                                                            <form-date
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="$t('ebs.sessions.edit.labels.labelTo')"
                                                                :name="`date-to-${index}`"
                                                                :hint-button="true"
                                                                :value="item.dateEndLocal"
                                                                :required="true"
                                                                :v-validate="`required|date_format:yyyy-MM-dd|${item.dateStartLocal && item.dateEndLocal && item.dateStartLocal !== item.dateEndLocal ? `after:${item.dateStartLocal}` :  ``}`"
                                                                :v-validate-custom-error="$t('ebs.sessions.edit.notify.validateCustomErrorTo')"
                                                                @emit-hint="setSessionOccurrenceItem"
                                                                @input="[setSessionOccurrenceItemDateEnd(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            >
                                                            </form-date>
                                                        </div>
                                                    </div>
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_time">
                                                            <form-time
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="' '"
                                                                :name="`time-to-${index}`"
                                                                :value="item.timeEndLocal"
                                                                :v-validate="{ required: true, date_format: 'HH:mm', after: (item.dateStartLocal === item.dateEndLocal && item.timeStartLocal) ? item.timeStartLocal : false }"
                                                                :placeholder="'00:00'"
                                                                :data-vv-as="$t('forms.dataVvAs.hour')"
                                                                @input="[setSessionOccurrenceItemTimeEnd(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <template v-if="differentLocation || differentTrainers">
                                        <div class="session__edit_details_dates_form_different">
                                            <component-wrapper :overlay="true">
                                                <div class="columns is-multiline is-variable is-4">
                                                    <template v-if="differentLocation">
                                                        <div class="column is-12">
                                                            <div class="session__edit_details_dates_form_different_location">
                                                                <form-select
                                                                    :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                    :label="$t('ebs.sessions.edit.labels.labelLocation')"
                                                                    :items="rooms"
                                                                    :name="`default-location-${index}`"
                                                                    :value="item.roomId"
                                                                    :required="true"
                                                                    :v-validate="'required'"
                                                                    :hint-button="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))"
                                                                    :data-vv-as="$t('forms.dataVvAs.location')"
                                                                    :get-items-getter="'getRoomsMappedIdNameStatus'"
                                                                    :get-items-getter-mapped="'getRoomsMappedLabelValue'"
                                                                    :get-items-dispatch="'getRooms'"
                                                                    :get-items-dispatch-data="['active']"
                                                                    :item-name="['room', 'rooms']"
                                                                    :search-queryable="false"
                                                                    :table-labels="[
                                                                {
                                                                    key: 'name',
                                                                    title: 'Name',
                                                                    type: 'string',
                                                                    sortable: true,
                                                                    queryable: true
                                                                },
                                                                {
                                                                    key: 'status',
                                                                    title: 'Status',
                                                                    type: 'string',
                                                                    sortable: true,
                                                                    queryable: true
                                                                }
                                                            ]"
                                                                    @emit-hint="setDifferentLocation(false, index)"
                                                                    @input="[setSessionOccurrenceItemRoomId(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                                    @input-table="[setSessionOccurrenceItemRoomId(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                                >
                                                                    <template v-if="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))"> {{
                                                                        $t('ebs.sessions.edit.text.sameLocation') }}
                                                                    </template>
                                                                </form-select>
                                                                <component-note
                                                                    v-if="listAvailabilityConflicts(index, 'room').length"
                                                                    :key="`session-edit-details-dates-form-location-conflicts-room-${index}`"
                                                                    :class="'session__edit_details_dates_form_different_location_conflicts session__edit_details_dates_form_different_location_conflicts--room'"
                                                                >
                                                                    <component-text-list
                                                                        :title="'Conflicts'"
                                                                        :html-items="listAvailabilityConflicts(index, 'room')"
                                                                    />
                                                                </component-note>
                                                            </div>
                                                        </div>
                                                    </template>
                                                    <template v-if="differentTrainers && sessionEnableTrainers">
                                                        <div class="column"><br></div>
                                                        <div class="column is-12">
                                                            <div class="session__edit_details_dates_form_different_trainers">
                                                                <form-select
                                                                    :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                    :label="$t('ebs.sessions.edit.labels.trainers')"
                                                                    :items="trainers"
                                                                    :multiple="true"
                                                                    :name="`default-trainers-${index}`"
                                                                    :value="sessionOccurrenceItemTrainers(index)"
                                                                    :required="true"
                                                                    :v-validate="'required'"
                                                                    :hint-button="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))"
                                                                    :data-vv-as="$t('forms.dataVvAs.trainers')"
                                                                    :get-pagination-getter="'getTrainersPagination'"
                                                                    :get-items-getter="'getTrainersMappedIdNameEmail'"
                                                                    :get-items-getter-mapped="'getTrainersMappedLabelValue'"
                                                                    :get-items-dispatch="'getTrainers'"
                                                                    :item-name="['trainer', 'trainers']"
                                                                    :search-queryable="false"
                                                                    :table-labels="[
                                                                {
                                                                    key: 'name',
                                                                    title: 'Name',
                                                                    type: 'string',
                                                                    sortable: true,
                                                                    queryable: true
                                                                },
                                                                {
                                                                    key: 'email',
                                                                    title: 'Email',
                                                                    type: 'string',
                                                                    sortable: true,
                                                                    queryable: true
                                                                }
                                                            ]"
                                                                    @emit-hint="setDifferentTrainers(false, index)"
                                                                    @input="[setSessionOccurrenceItemTrainersId(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                                    @input-table="[setSessionOccurrenceItemTrainersId(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                                >
                                                                    <template v-if="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))">{{ $t('ebs.sessions.edit.text.sameTrainers')
                                                                        }}
                                                                    </template>
                                                                </form-select>
                                                                <component-note
                                                                    v-if="listAvailabilityConflicts(index, 'trainer').length"
                                                                    :key="`session-edit-details-dates-form-location-conflicts-trainer-${index}`"
                                                                    :class="'session__edit_details_dates_form_different_location_conflicts session__edit_details_dates_form_different_location_conflicts--trainer'"
                                                                >
                                                                    <component-text-list
                                                                        :title="'Conflicts'"
                                                                        :html-items="listAvailabilityConflicts(index, 'trainer')"
                                                                    />
                                                                </component-note>
                                                            </div>
                                                        </div>
                                                    </template>
                                                </div>
                                            </component-wrapper>
                                        </div>
                                    </template>
                                </div>
                            </template>
                            <template v-else-if="sessionSessionType === 'virtual'">
                                <div class="session__edit_details_dates_main_virtual">
                                    <div class="columns is-variable">
                                        <div class="column is-narrow">
                                            <div :class="['session__edit_details_dates_form', 'session__edit_details_dates_form--from', errors.first(`hidden-${index}`) ? 'session__edit_details_dates_form--error' : '']">
                                                <div class="columns is-variable is-2">
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_date">
                                                            <form-date
                                                                :ref="'fromDate'"
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="$t('ebs.sessions.edit.labels.labelFrom')"
                                                                :name="`date-from-${index}`"
                                                                :required="true"
                                                                :value="item.dateStartLocal"
                                                                :v-validate="`required|date_format:yyyy-MM-dd|${item.dateStartLocal !== item.dateEndLocal ? `before:${item.dateEndLocal}` :  ``}`"
                                                                :v-validate-custom-error="$t('ebs.sessions.edit.notify.validateCustomError')"
                                                                @input="[setSessionOccurrenceItemDateStart(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            ></form-date>
                                                        </div>
                                                    </div>
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_time">
                                                            <form-time
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="' '"
                                                                :name="`time-from-${index}`"
                                                                :value="item.timeStartLocal"
                                                                :v-validate="{ required: true, date_format: 'HH:mm', before: (item.dateStartLocal === item.dateEndLocal && item.timeEndLocal) ? item.timeEndLocal : false }"
                                                                :placeholder="'00:00'"
                                                                :data-vv-as="$t('forms.dataVvAs.hour')"
                                                                @input="[setSessionOccurrenceItemTimeStart(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="column has-text-centered">
                                            <div class="session__edit_details_dates_form_time_separator"></div>
                                        </div>
                                        <div class="column is-narrow">
                                            <div :class="['session__edit_details_dates_form', 'session__edit_details_dates_form--to', errors.first(`hidden-${index}`) ? 'session__edit_details_dates_form--error' : '']">
                                                <div class="columns columns--oneline is-variable is-2">
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_date">
                                                            <form-date
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="$t('ebs.sessions.edit.labels.labelTo')"
                                                                :name="`date-to-${index}`"
                                                                :hint-button="true"
                                                                :value="item.dateEndLocal"
                                                                :required="true"
                                                                :v-validate="`required|date_format:yyyy-MM-dd|${item.dateStartLocal !== item.dateEndLocal ? `after:${item.dateStartLocal}` :  ``}`"
                                                                :v-validate-custom-error="$t('ebs.sessions.edit.notify.validateCustomErrorTo')"
                                                                @emit-hint="setSessionOccurrenceItem"
                                                                @input="[setSessionOccurrenceItemDateEnd(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            >
                                                            </form-date>
                                                        </div>
                                                    </div>
                                                    <div class="column is-narrow">
                                                        <div class="session__edit_details_dates_form_time">
                                                            <form-time
                                                                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                                                :label="' '"
                                                                :name="`time-to-${index}`"
                                                                :value="item.timeEndLocal"
                                                                :v-validate="{ required: true, date_format: 'HH:mm', after: (item.dateStartLocal === item.dateEndLocal && item.timeStartLocal) ? item.timeStartLocal : false }"
                                                                :placeholder="'00:00'"
                                                                :data-vv-as="$t('forms.dataVvAs.hour')"
                                                                @input="[setSessionOccurrenceItemTimeEnd(index, ...arguments), checkAvailabilityLocationAndTrainers(index)]"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <form-text
                                        :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                        :name="`session-edit-details-meeting-link-${index}`"
                                        :label="'Meeting link'"
                                        :required="true"
                                        :v-validate="{ required: true, regex: urlRegex }"
                                        :value="item.meetingLink"
                                        :data-vv-as="'meeting link'"
                                        @input="setSessionOccurrenceItemMeetingLink(index, ...arguments)"
                                    >This is the link that the user will be redirected to when the select “Join online”.
                                    </form-text>
                                    <form-text
                                        :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                        :name="`session-edit-details-platform-${index}`"
                                        :label="'Platform'"
                                        :required="true"
                                        :v-validate="{ required: true }"
                                        :value="item.platform"
                                        @input="setSessionOccurrenceItemPlatform(index, ...arguments)"
                                    >This will be the text displayed as the location for the session details. e.g - Zoom, Skype, Teams etc.
                                    </form-text>
                                    <form-editor
                                        :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                        :name="`session-edit-details-details-${index}`"
                                        :label="'Virtual access details'"
                                        :value="item.virtualAccessDetails"
                                        @input="setSessionOccurrenceItemDetails(index, ...arguments)"
                                    >Any further information the learner will need, such as passwords, can be placed here.\nThis will be displayed for learners who have book onto the session.
                                    </form-editor>
                                    <template v-if="sessionEnableTrainers">
                                        <form-select
                                            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                            :label="$t('ebs.sessions.edit.labels.trainers')"
                                            :multiple="true"
                                            :name="`session-edit-details-trainers-${index}`"
                                            :value="sessionOccurrenceItemTrainers(index)"
                                            :required="true"
                                            :v-validate="'required'"
                                            :data-vv-as="$t('forms.dataVvAs.trainers')"
                                            :get-pagination-getter="'getTrainersPagination'"
                                            :get-items-getter="'getTrainersMappedIdNameEmail'"
                                            :get-items-getter-mapped="'getTrainersMappedLabelValue'"
                                            :get-items-dispatch="'getTrainers'"
                                            :item-name="['trainer', 'trainers']"
                                            :search-queryable="false"
                                            :table-labels="[
                                                {
                                                    key: 'name',
                                                    title: 'Name',
                                                    type: 'string',
                                                    sortable: true,
                                                    queryable: true
                                                },
                                                {
                                                    key: 'email',
                                                    title: 'Email',
                                                    type: 'string',
                                                    sortable: true,
                                                    queryable: true
                                                }
                                            ]"
                                            @input="setSessionOccurrenceItemTrainersId(index, ...arguments)"
                                            @input-table="setSessionOccurrenceItemTrainersId(index, ...arguments)"
                                        ></form-select>
                                    </template>
                                </div>
                            </template>
                            <div class="session__edit_details_dates_main_actions">
                                <div class="columns is-multiline">
                                    <div class="column is-12">
                                        <component-button
                                            :align="'left'"
                                            v-if="sessionOccurrenceItems.length > 1"
                                            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                                            :name="'session-edit-details-remove-occurrence'"
                                            :icon="true"
                                            :icon-name="'minus-circle'"
                                            :theme="'default'"
                                            :text="true"
                                            :size="'tiny'"
                                            @click="removeSessionOccurrenceItem(index)"
                                        >Remove this date
                                        </component-button>
                                    </div>
                                    <template v-if="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))">
                                        <div class="column is-12">
                                            <component-button
                                                :align="'left'"
                                                :name="'session-edit-details-add-occurrence'"
                                                :icon="true"
                                                :icon-name="'plus-circle'"
                                                :theme="'default'"
                                                :text="true"
                                                :size="'tiny'"
                                                @click="duplicateSessionOccurenceItem"
                                            >Add another date for this session</component-button>
                                            <component-text :size="'tiny'">Does this event span over multiple times? If yes then go ahead and add another time slot.</component-text>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </transition>
                </li>
            </transition-group>
        </div>
        <template v-if="sessionSessionType === 'face_to_face'">
            <div class="session__edit_details_default">
                <transition-group name="fade" mode="out-in">
                    <template v-if="!differentLocation">
                        <form-select
                            :key="'session-edit-details-room-main-form'"
                            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                            :label="$t('ebs.sessions.edit.labels.labelLocation')"
                            :items="rooms"
                            :name="'default-location'"
                            :value="sessionOccurrenceItems[0].roomId"
                            :required="true"
                            :v-validate="'required'"
                            :hint-button="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))"
                            :data-vv-as="$t('forms.dataVvAs.location')"
                            :get-items-getter="'getRoomsMappedIdNameStatus'"
                            :get-items-getter-mapped="'getRoomsMappedLabelValue'"
                            :get-items-dispatch="'getRooms'"
                            :get-items-dispatch-data="['active']"
                            :item-name="['room', 'rooms']"
                            :search-queryable="false"
                            :table-labels="[
                                {
                                    key: 'name',
                                    title: 'Name',
                                    type: 'string',
                                    sortable: true,
                                    queryable: true
                                },
                                {
                                    key: 'status',
                                    title: 'Status',
                                    type: 'string',
                                    sortable: true,
                                    queryable: true
                                }
                            ]"
                            @emit-hint="setDifferentLocation(true)"
                            @input="[setSessionOccurrenceItemRoomId(0, ...arguments), checkAvailabilityLocationAndTrainers(0)]"
                            @input-table="[setSessionOccurrenceItemRoomId(0, ...arguments), checkAvailabilityLocationAndTrainers(0)]"
                        >
                            <template v-if="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))">{{ $t('ebs.sessions.edit.text.differentLocation') }}
                            </template>
                        </form-select>
                        <component-note
                            v-if="listAvailabilityConflicts(0, 'room').length"
                            :key="'session-edit-details-room-main-conflicts'"
                            :class="'session__edit_details_default_conflicts session__edit_details_default_conflicts--room'"
                        >
                            <component-text-list
                                :title="'Conflicts'"
                                :html-items="listAvailabilityConflicts(0, 'room')"
                            />
                        </component-note>
                    </template>
                </transition-group>
                <transition-group name="fade" mode="out-in">
                    <template v-if="!differentTrainers && sessionEnableTrainers">
                        <form-select
                            :key="'session-edit-details-trainers-main-form'"
                            :label="$t('ebs.sessions.edit.labels.trainers')"
                            :items="trainers"
                            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                            :multiple="true"
                            :name="'default-trainer'"
                            :value="sessionOccurrenceItemTrainers(0)"
                            :required="true"
                            :v-validate="'required'"
                            :hint-button="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))"
                            :data-vv-as="$t('forms.dataVvAs.trainers')"
                            :get-pagination-getter="'getTrainersPagination'"
                            :get-items-getter="'getTrainersMappedIdNameEmail'"
                            :get-items-getter-mapped="'getTrainersMappedLabelValue'"
                            :get-items-dispatch="'getTrainers'"
                            :item-name="['trainer', 'trainers']"
                            :search-queryable="false"
                            :table-labels="[
                                {
                                    key: 'name',
                                    title: 'Name',
                                    type: 'string',
                                    sortable: true,
                                    queryable: true
                                },
                                {
                                    key: 'email',
                                    title: 'Email',
                                    type: 'string',
                                    sortable: true,
                                    queryable: true
                                }
                            ]"
                            @emit-hint="setDifferentTrainers(true)"
                            @input="[setSessionOccurrenceItemTrainersId(0, ...arguments), checkAvailabilityLocationAndTrainers(0)]"
                            @input-table="[setSessionOccurrenceItemTrainersId(0, ...arguments), checkAvailabilityLocationAndTrainers(0)]"
                        >
                            <template v-if="isAddMode || (isEditMode && (isSessionUpcoming || isSessionInProgress || isSessionDraft))">{{ $t('ebs.sessions.edit.text.differentTrainers') }}
                            </template>
                        </form-select>
                        <component-note
                            v-if="listAvailabilityConflicts(0, 'trainer').length"
                            :key="'session-edit-details-trainers-main-conflicts'"
                            :class="'session__edit_details_default_conflicts session__edit_details_default_conflicts--trainer'"
                        >
                            <component-text-list
                                :title="'Conflicts'"
                                :html-items="listAvailabilityConflicts(0, 'trainer')"
                            />
                        </component-note>
                    </template>
                </transition-group>
                <div class="session__edit_details_default_workinghours">
                    <component-note
                        v-if="checkTrainerWorkingHours().length"
                        :key="'session-edit-details-default-workinghours'"
                        :class="'session__edit_details_default_workinghours_note'"
                    >
                        <span>{{ $t('ebs.sessions.edit.text.thisDateTime') }} {{ checkTrainerWorkingHours().length === 1 ? this.$t('ebs.sessions.edit.text.trainer'): this.$t('ebs.sessions.edit.text.trainers') }} </span>
                        <strong
                            v-for="(trainer, index) in checkTrainerWorkingHours()"
                            :key="index"
                        >{{ index === 0 ? trainer : `, ${trainer}` }}</strong>
                        <span> {{ $t('ebs.sessions.edit.text.standardWorkingHours') }}</span>
                    </component-note>
                </div>
            </div>
        </template>
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.title.visibility') }}</component-title>
        <form-checkbox
            :label="'Status'"
            :name="'status'"
            :switcher="true"
            :activeInactive="true"
            :disabled="sessionHasBookings || isSessionCancelled || isSessionEnded"
            :value="sessionStatus === 'active' || sessionStatus === 'upcoming' || sessionStatus === 'in_progress' || sessionStatus === 'ended'"
            @input="updateSessionStatus"
        >{{ isSecondStageTokenFromOldLMS && !sessionEventHasCourses ? 'Field is disabled because event is not assigned to any course' : (sessionHasBookings ? 'Visibility can be toggled if there are no learners booked onto this session.' : '') }}
        </form-checkbox>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import PopNotify from '@/components/default/shared/PopNotify'
import FormSelect from '@/components/default/forms/FormSelect'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormTime from '@/components/default/forms/FormTime'
import FormText from '@/components/default/forms/FormText'
import FormDate from '@/components/default/forms/FormDate'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import ComponentTextList from '@/components/default/shared/ComponentTextList'
import ComponentNote from '@/components/default/shared/ComponentNote'
import { toISODate } from '@/functions'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormEditor from '@/components/default/forms/FormEditor'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentText from '@/components/default/shared/ComponentText'
import { urlRegex } from '@/variables'
import { User } from '@/User'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'

export default {
  name: 'session-edit-details',
  components: {
    ComponentWrapper,
    ComponentText,
    ComponentButton,
    FormEditor,
    FormRadioHorizontal,
    ComponentNote,
    ComponentTextList,
    FormDate,
    FormText,
    FormTime,
    ComponentTitle,
    FormSelect,
    PopNotify,
    FormCheckbox
  },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  data () {
    return {
      differentLocation: false,
      differentTrainers: false,
      availabilityConflicts: [],
      virtualOccurrenceActive: 0,
      urlRegex,
      user: new User()
    }
  },
  computed: {
    isAddMode () {
      return this.$route.name.includes('session-add')
    },
    isEditMode () {
      return this.$route.name.includes('session-edit')
    },
    isSessionDraft () {
      return this.sessionDefaultStatus === 'draft'
    },
    isSessionUpcoming () {
      return this.sessionDefaultStatus === 'upcoming'
    },
    isSessionCancelled () {
      return this.sessionDefaultStatus === 'cancelled'
    },
    isSessionEnded () {
      return this.sessionDefaultStatus === 'ended'
    },
    isSessionInProgress () {
      return this.sessionDefaultStatus === 'in_progress'
    },
    isSecondStageTokenFromOldLMS () {
      return this.$store.getters.getAuthUserIsSecondStageTokenOldLMS
    },
    sessionId () {
      return this.$store.getters.getSessionId
    },
    sessionSessionType: {
      get () {
        return this.$store.getters.getSessionSessionType
      },
      set (value) {
        this.setSessionBookingLimitType(value === 'face_to_face' ? 'roomBased' : 'unlimited')
        this.$store.dispatch('setSessionSessionType', value)
      }
    },
    sessionStatus: {
      get () {
        return this.$store.getters.getSessionStatus
      }
    },
    sessionDefaultStatus: {
      get () {
        return this.$store.getters.getSessionDefaultStatus
      }
    },
    sessionOccurrenceItems: {
      get () {
        return this.$store.getters.getSessionOccurrenceItems
      }
    },
    sessionEnableTrainers: {
      get () {
        return this.$store.getters.getSessionEnableTrainers
      }
    },
    sessionEventHasCourses () {
      return this.$store.getters.getSessionEventHasCourses
    },
    watchContent () {
      return this.sessionStatus + this.sessionOccurrenceItems + this.sessionEnableTrainers
    },
    rootItemName () {
      return 'session'
    },
    //
    sessionHasBookings () {
      return this.$store.getters.getSessionHasBookedLearnersActive
    },
    rawRooms () {
      return this.$store.getters.getRooms
    },
    rooms () {
      let objectData = this.rawRooms
      let objectDataArray = []
      if (objectData) {
        objectDataArray = objectData.map(function (object) {
          return {
            label: `${object.venue.name} - ${object.name}`,
            value: object.id
          }
        })
      }
      return objectDataArray
    },
    rawTrainers () {
      return this.$store.getters.getTrainers
    },
    trainers () {
      let objectData = this.rawTrainers
      let objectDataArray = []
      if (objectData) {
        objectDataArray = objectData.map(function (object) {
          return {
            label: `${object.name} (${object.email})`,
            value: object.id
          }
        })
      }
      return objectDataArray
    },
    watchComputedRoomsTrainers () {
      return this.rawRooms + this.rawTrainers
    }
  },
  methods: {
    checkTrainerWorkingHours () {
      let notValidatedTrainers = []
      const occurrences = this.sessionOccurrenceItems
      const allTrainers = this.rawTrainers
      //
      if (!occurrences || !allTrainers) return notValidatedTrainers
      for (let i = 0; i < occurrences.length; i++) {
        const occurrence = occurrences[i]
        const date = toISODate(occurrence.datetimeStart)
        const datetimeStart = occurrence.datetimeStart
        const datetimeEnd = occurrence.datetimeEnd
        //
        if (!date || !datetimeStart || !datetimeEnd) return notValidatedTrainers
        let tempDate = (new Date(date).getDay() - 1)
        let day = tempDate > -1 ? tempDate : 6
        const timeStart = datetimeStart.split(' ')[1]
        const timeEnd = datetimeEnd.split(' ')[1]
        const trainers = occurrence.trainers
        //
        for (let j = 0; j < trainers.length; j++) {
          let trainer = trainers[j]
          let trainerWorkFrom
          let trainerWorkTo
          if (!trainer) continue
          trainer = allTrainers.filter(obj => {
            return obj.id === trainer.trainerId
          })[0]
          if (!trainer) return notValidatedTrainers
          trainerWorkFrom = trainer.workingHours[day].workFrom
          trainerWorkTo = trainer.workingHours[day].workTo
          //
          if (trainerWorkFrom > timeStart || trainerWorkTo < timeEnd) {
            if (!notValidatedTrainers.includes(trainer.name)) {
              notValidatedTrainers.push(trainer.name)
            }
          }
        }
      }
      return notValidatedTrainers
    },
    //
    checkDifferentLocationAndTrainers () {
      const occurrenceItems = this.sessionOccurrenceItems
      let itemsRoom = []
      let itemsTrainers = []
      for (let i = 0; i < occurrenceItems.length; i++) {
        itemsRoom[i] = occurrenceItems[i].roomId
        itemsTrainers[i] = occurrenceItems[i].trainers.map(obj => obj.trainerId)
      }
      for (let j = 0; j < itemsRoom.length; j++) {
        for (let jk = 0; jk < itemsRoom.length; jk++) {
          if (itemsRoom[j] !== itemsRoom[jk]) {
            this.differentLocation = true
          }
        }
      }
      for (let k = 0; k < itemsTrainers.length; k++) {
        for (let kk = 0; kk < itemsTrainers[k].length; kk++) {
          for (let kj = 0; kj < itemsTrainers.length; kj++) {
            if (itemsTrainers[kj].indexOf(itemsTrainers[k][kk]) === -1) {
              this.differentTrainers = true
            }
          }
        }
      }
    },
    checkAvailabilityLocationAndTrainers (index) {
      const occurrenceItems = this.sessionOccurrenceItems
      if (index) {
        const occurrenceItem = occurrenceItems[index]
        const datetimeStart = occurrenceItem.datetimeStart
        const datetimeEnd = occurrenceItem.datetimeEnd
        const roomId = occurrenceItem.roomId
        const trainersId = occurrenceItem.trainers.map(obj => obj.trainerId)
        const occurrenceId = this.sessionId

        if (datetimeStart && datetimeEnd && roomId && trainersId) {
          this.$store.dispatch('checkSessionAssets', { datetimeStart, datetimeEnd, roomId, trainersId, occurrenceId }).then(response => {
            const availabilityConflicts = this.availabilityConflicts
            let data = response

            if (!availabilityConflicts[index]) {
              availabilityConflicts.push(data)
            } else {
              if (data.length) {
                availabilityConflicts.splice(index, 1, data)
              } else {
                availabilityConflicts.splice(index, 1)
              }
            }
            this.$forceUpdate()
            this.rerenderIndex++
          })
        }
      } else {
        for (let i in occurrenceItems) {
          this.checkAvailabilityLocationAndTrainers(i)
        }
      }
    },
    listAvailabilityConflicts (index, key) {
      let conflictsArray = []
      const availabilityConflicts = this.availabilityConflicts
      const selectedConflicts = availabilityConflicts[index]
      const filteredSelectedConflicts = selectedConflicts ? selectedConflicts.filter(obj => obj[key + 'Id'] !== '') : []

      for (let index in filteredSelectedConflicts) {
        conflictsArray.push(`${key === 'room' ? 'Room' : ''}${key === 'trainer' ? 'Trainer' : ''} <strong>${filteredSelectedConflicts[index][key + 'Name']}</strong> is already in use by <strong>${filteredSelectedConflicts[index].eventName}</strong> event at <strong>${this.toLocaleDateTime(filteredSelectedConflicts[index].datetimeStart)} ${this.toLocaleTime(filteredSelectedConflicts[index].datetimeStart)}</strong> - <strong>${this.toLocaleDateTime(filteredSelectedConflicts[index].datetimeEnd)} ${this.toLocaleTime(filteredSelectedConflicts[index].datetimeEnd)}</strong> session.`)
      }

      return conflictsArray
    },
    setDifferentLocation (value, index) {
      this.differentLocation = value
      if (typeof index !== 'undefined') {
        this.setSessionOccurrenceItemRoomIdAll(index)
      }
    },
    setDifferentTrainers (value, index) {
      this.differentTrainers = value
      if (typeof index !== 'undefined') {
        this.setSessionOccurrenceItemTrainersIdAll(index)
      }
    },
    //
    setSessionOccurrenceItem () {
      this.$store.dispatch('setSessionOccurrenceItem')
      if (!this.differentLocation) {
        this.setSessionOccurrenceItemRoomIdAll()
      }
      if (!this.differentTrainers) {
        this.setSessionOccurrenceItemTrainersIdAll()
      }
      if (this.sessionSessionType === 'virtual') {
        this.setVirtualOccurrenceActive(this.sessionOccurrenceItems.length - 1)
      }
    },
    duplicateSessionOccurenceItem () {
      this.$store.dispatch('duplicateSessionOccurrenceItem')
      this.setVirtualOccurrenceActive(this.sessionOccurrenceItems.length - 1)
    },
    setVirtualOccurrenceActive (value) {
      this.virtualOccurrenceActive = this.virtualOccurrenceActive === value ? '' : value
    },
    removeSessionOccurrenceItem (key) {
      this.$store.dispatch('removeSessionOccurrenceItem', key)
      if (this.sessionSessionType === 'virtual') {
        this.setVirtualOccurrenceActive(key - 1)
      }
    },
    setSessionOccurrenceItemDateStart (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemDateStart', { index: index, data: data })
    },
    setSessionOccurrenceItemDateEnd (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemDateEnd', { index: index, data: data })
    },
    setSessionOccurrenceItemTimeStart (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemTimeStart', { index: index, data: data })
    },
    setSessionOccurrenceItemTimeEnd (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemTimeEnd', { index: index, data: data })
    },
    setSessionOccurrenceItemMeetingLink (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemMeetingLink', { index: index, data: data })
    },
    setSessionOccurrenceItemPlatform (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemPlatform', { index: index, data: data })
    },
    setSessionOccurrenceItemDetails (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemDetails', { index: index, data: data })
    },
    setSessionBookingLimitType (value) {
      this.$store.dispatch('setSessionBookingLimitType', value)
    },
    setSessionOccurrenceItemRoomId (index, data) {
      const object = this.rawRooms.filter(obj => {
        return obj.id === data.value
      })
      this.$store.dispatch('setSessionOccurrenceItemRoomId', { index: index, data: data })
      this.$store.dispatch('setSessionOccurrenceItemVenueId', { index: index, data: object[0].venue.id })
      if (!this.differentLocation) {
        this.setSessionOccurrenceItemRoomIdAll()
      }
      if (!this.differentTrainers) {
        this.setSessionOccurrenceItemTrainersIdAll()
      }
    },
    setSessionOccurrenceItemRoomIdAll (key) {
      this.$store.dispatch('setSessionOccurrenceItemRoomIdAll', key)
      this.$store.dispatch('setSessionOccurrenceItemVenueIdAll')
    },
    setSessionOccurrenceItemTrainersId (index, data) {
      this.$store.dispatch('setSessionOccurrenceItemTrainersId', { index: index, data: data })
      if (!this.differentLocation) {
        this.setSessionOccurrenceItemRoomIdAll()
      }
      if (!this.differentTrainers) {
        this.setSessionOccurrenceItemTrainersIdAll()
      }
    },
    setSessionOccurrenceItemTrainersIdAll (key) {
      this.$store.dispatch('setSessionOccurrenceItemTrainersIdAll', key)
    },
    //
    updateSessionStatus (value) {
      const defaultStatus = this.sessionDefaultStatus
      let newStatus = value ? (defaultStatus && defaultStatus !== 'draft' ? defaultStatus : 'active') : 'draft'
      this.$store.dispatch('setSessionStatus', newStatus)
    },
    sessionReset () {
      this.$store.dispatch('resetSession')
    },
    sessionSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-session-add')) {
            this.$store.dispatch('createSession')
          } else if (this.$route.name.includes('ebs-session-edit')) {
            this.$store.dispatch('updateSession')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    },
    //
    sessionOccurrenceItemTrainers (index) {
      return this.$store.getters.getSessionOccurrenceItemTrainers(index)
    }
  },
  watch: {
    sessionOccurrenceItems (value) {
      this.checkDifferentLocationAndTrainers()
    },
    watchComputedRoomsTrainers () {
      if (this.rawRooms.length && this.rawTrainers.length) {
        const occurrenceItems = this.sessionOccurrenceItems
        for (let index in occurrenceItems) {
          this.checkAvailabilityLocationAndTrainers(index)
        }
      }
    },
    sessionId (value) {
      if (value) {
        this.checkAvailabilityLocationAndTrainers()
      }
    }
  },
  created () {
    const self = this
    this.$validator.extend('checkDatesConflict', {
      getMessage (field, val) {
        return 'Dates & times in the session item must be not in conflict with other ones.'
      },
      validate (value, field) {
        let occurrenceItems = JSON.parse(JSON.stringify(self.sessionOccurrenceItems))
        if (occurrenceItems.length) {
          const currentOccurrenceItem = occurrenceItems[value]
          const currentOccurrenceItemStart = new Date(currentOccurrenceItem.datetimeStart)
          const currentOccurrenceItemEnd = new Date(currentOccurrenceItem.datetimeEnd)
          occurrenceItems.splice(value, 1)
          for (let index in occurrenceItems) {
            const loopOccurrenceItemStart = new Date(occurrenceItems[index].datetimeStart)
            const loopOccurrenceItemEnd = new Date(occurrenceItems[index].datetimeEnd)
            if (
              (
                currentOccurrenceItemStart > loopOccurrenceItemStart &&
            currentOccurrenceItemStart < loopOccurrenceItemEnd
              ) || (
                currentOccurrenceItemEnd > loopOccurrenceItemStart &&
            currentOccurrenceItemEnd < loopOccurrenceItemEnd)
            ) {
              return false
            }
          }
        }
        return true
      }
    })
  },
  mounted () {
    this.checkDifferentLocationAndTrainers()
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/sessions/sessioneditdetails";
</style>
